import { useEffect, useState, useCallback } from "react";
import { Download } from "lucide-react";
import { api } from "@/lib/api";
import { PageHead, TableSkeleton, Pager, KpiCard } from "./ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, HeartHandshake, ShieldOff } from "lucide-react";

const PS = 30;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function download(path) {
  const token = localStorage.getItem("mv_token") || "";
  fetch(`${BACKEND}/api${path}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.blob()).then((b) => {
      const a = document.createElement("a"); a.href = URL.createObjectURL(b);
      a.download = path.split("/").pop().includes("audit") ? "audit_logs.csv" : "consent_records.csv"; a.click();
    });
}

export default function AdminCompliance() {
  return (
    <div data-testid="admin-compliance">
      <PageHead title="Compliance" subtitle="Audit logs & consent records (GDPR Article 30)" />
      <Tabs defaultValue="audit">
        <TabsList>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Logs</TabsTrigger>
          <TabsTrigger value="consents" data-testid="tab-consents">Consent Records</TabsTrigger>
        </TabsList>
        <TabsContent value="audit"><AuditLogs /></TabsContent>
        <TabsContent value="consents"><ConsentRecords /></TabsContent>
      </Tabs>
    </div>
  );
}

function AuditLogs() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("all");
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    const p = new URLSearchParams({ page, page_size: PS });
    if (action !== "all") p.set("action", action);
    if (search) p.set("search", search);
    setData(null);
    api.get(`/admin/compliance/audit-logs?${p}`).then((r) => setData(r.data));
  }, [page, action, search]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-4">
      {data?.stats && (
        <div className="grid grid-cols-2 gap-4 mb-4">
          <KpiCard label="Total Log Entries" value={data.stats.total} icon={FileText} tint="#264653" />
          <KpiCard label="This Month" value={data.stats.this_month} icon={FileText} tint="#0D5C75" />
        </div>
      )}
      <div className="flex flex-wrap gap-3 mb-4">
        <Input placeholder="Search actor…" value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} className="max-w-xs" data-testid="audit-search" />
        <Select value={action} onValueChange={(v) => { setPage(1); setAction(v); }}>
          <SelectTrigger className="w-48" data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "ADMIN_ACTION", "consent", "phi", "user.erasure", "booking"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={() => download("/admin/compliance/audit-logs/export")} data-testid="export-audit-btn"><Download size={15} className="mr-1" /> Export CSV</Button>
      </div>
      <div className="rounded-2xl bg-white p-4 shadow-sm border border-black/5">
        {!data ? <TableSkeleton /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Timestamp</TableHead><TableHead>Actor</TableHead><TableHead>Action</TableHead>
                <TableHead>Resource</TableHead><TableHead>IP (hashed)</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.logs.map((l, i) => (
                  <TableRow key={i} data-testid="audit-row">
                    <TableCell className="text-xs">{(l.timestamp || "").slice(0, 19).replace("T", " ")}</TableCell>
                    <TableCell className="max-w-[180px] truncate">{l.actor}</TableCell>
                    <TableCell className="font-medium">{l.sub_action || l.action}</TableCell>
                    <TableCell className="text-muted-foreground">{l.resource_type}</TableCell>
                    <TableCell className="font-mono text-[10px] text-muted-foreground">{(l.ip_hash || "").slice(0, 16)}…</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pager page={page} total={data.total} pageSize={PS} onPage={setPage} />
          </div>
        )}
      </div>
    </div>
  );
}

function ConsentRecords() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [ctype, setCtype] = useState("all");

  const load = useCallback(() => {
    const p = new URLSearchParams({ page, page_size: PS });
    if (ctype !== "all") p.set("consent_type", ctype);
    setData(null);
    api.get(`/admin/compliance/consents?${p}`).then((r) => setData(r.data));
  }, [page, ctype]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-4">
      {data?.stats && (
        <div className="grid grid-cols-3 gap-4 mb-4">
          <KpiCard label="Total Records" value={data.stats.total} icon={FileText} tint="#264653" />
          <KpiCard label="Active Health Consents" value={data.stats.active_health} icon={HeartHandshake} tint="#2A9D8F" />
          <KpiCard label="Erasure Requests" value={data.stats.erasure} icon={ShieldOff} tint="#E76F51" />
        </div>
      )}
      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={ctype} onValueChange={(v) => { setPage(1); setCtype(v); }}>
          <SelectTrigger className="w-48" data-testid="consent-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "cookies", "health_data", "marketing", "terms", "privacy"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={() => download("/admin/compliance/consents/export")} data-testid="export-consents-btn"><Download size={15} className="mr-1" /> Export CSV</Button>
      </div>
      <div className="rounded-2xl bg-white p-4 shadow-sm border border-black/5">
        {!data ? <TableSkeleton /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Timestamp</TableHead><TableHead>User</TableHead><TableHead>Type</TableHead>
                <TableHead>Status</TableHead><TableHead>IP (hashed)</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.consents.map((c, i) => (
                  <TableRow key={i} data-testid="consent-row">
                    <TableCell className="text-xs">{(c.timestamp || "").slice(0, 19).replace("T", " ")}</TableCell>
                    <TableCell className="max-w-[160px] truncate">{c.user_id || "Anonymous"}</TableCell>
                    <TableCell>{c.consent_type}</TableCell>
                    <TableCell className={c.granted ? "text-emerald-600" : "text-rose-500"}>{c.granted ? "Granted" : "Denied"}</TableCell>
                    <TableCell className="font-mono text-[10px] text-muted-foreground">{(c.ip_hash || "").slice(0, 16)}…</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pager page={page} total={data.total} pageSize={PS} onPage={setPage} />
          </div>
        )}
      </div>
    </div>
  );
}
